import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { clearB2BServiceDetails } from "./b2bserviceSlice";
import {
  createB2BService,
  getAllActiveB2BServices,
  getAllB2BServices,
  getB2BServiceByID,
  updateB2BService,
} from "./service/b2bservice.services";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import { closed } from "../../components/drawer/drawerSlice";
import IconComponent from "../../components/common/IconComponent";

const CreateB2BService: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const B2BServiceDetail = useAppSelector(
    (state) => state.b2bservice.B2BServiceDetail,
  );
  const B2BServiceID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.b2bservice.formLoading);
  const isIndirect = useAppSelector((state) => state.drawer.isIndirect);

  const [formKey, setFormKey] = React.useState<number>(1);

  useEffect(() => {
    return () => {
      dispatch(clearB2BServiceDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    setFormKey((prev) => prev + 1);
  }, [B2BServiceDetail]);

  useEffect(() => {
    if (B2BServiceID) {
      dispatch(getB2BServiceByID(B2BServiceID));
    }
  }, [B2BServiceID]);

  const handleSubmit = async (values: any) => {
    try {
      if (!B2BServiceID) {
        const insertPayload = {
          ServiceName: values.ServiceName,
          IsActive: values?.IsActive === 1,
        };
        const response = await dispatch(createB2BService(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearB2BServiceDetails());
          if (isIndirect) {
            dispatch(getAllActiveB2BServices());
          } else {
            dispatch(getAllB2BServices());
          }
        }
      } else {
        const editPayload = {
          ID: B2BServiceID,
          ServiceName: values.ServiceName,
          IsActive: values?.IsActive === 1,
        };
        const response = await dispatch(updateB2BService(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearB2BServiceDetails());
          dispatch(getAllB2BServices());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={B2BServiceDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
              <GridLayoutItem className="mui-drawer-header">
                <Typography.h4 className="m-0">
                  {B2BServiceID ? "Update B2B Service" : "Add B2B Service"}
                </Typography.h4>
                <Button
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                >
                  <IconComponent icon={RxCross1} />
                </Button>
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  name="ServiceName"
                  label="Service Name"
                  placeholder="i.e. Test Service"
                  component={FormTextField}
                  validator={requiredValidator}
                  astrike={true}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  id={"IsActive"}
                  name={"IsActive"}
                  label={"Status"}
                  astrike={true}
                  component={FormSelectionField}
                  validator={requiredValidator}
                  options={STATUSARRAY?.map((status: any) => {
                    return {
                      value: status?.value,
                      label: status?.label,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  textAlign: "start",
                  marginTop: "20px",
                  width: "100%",
                }}
              >
                <ButtonWithLoading
                  label={B2BServiceID ? "Update" : "Save"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || formLoading}
                  loading={formLoading}
                />
                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateB2BService;
